import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate, formatDisplayDateTime } from "src/utils/dateTime";
import { Box, HBox, VBox } from "@ui/box/Box";
import { IconMastercard } from "@ui/icons/IconMastercard";
import { IconVisa } from "@ui/icons/IconVisa";
import { Table } from "@ui/table/Table";
import { Typo } from "@ui/text/Typo";
import { useRef, useEffect, useLayoutEffect, useState } from "react";
import { CircleAlert } from "lucide-react";
import { CountryClip } from "../icons/CountryClip";
import { HoverTooltip, FailReasonHint } from "../HoverTooltip";
import { S_DECTA_SESSION_POPUP } from "../popup/DectaSessionPopup";
import { StatusBadge } from "../StatusBadge";

// ── Fail reason helpers ───────────────────────────────────────────────

// ── Fail reason helpers ───────────────────────────────────────────────
const cleanRaw = (raw: string): string =>
	raw
		.replace(/[\r\n\t]+/g, " ")
		.replace(/\s{2,}/g, " ")
		.trim();

const parseFailReason = (raw: string): [string, string][] => {
	const cleaned = cleanRaw(raw);

	// No colon at all → not a key:value format
	if (!cleaned.includes(":")) return [];

	// Split on ", key:" boundaries — value itself may contain commas
	const result: [string, string][] = [];
	const re = /(?:^|,\s*)([^:,]+?):\s*/g;
	const keys: { key: string; valueStart: number }[] = [];
	let m: RegExpExecArray | null;
	while ((m = re.exec(cleaned)) !== null) {
		keys.push({ key: m[1].trim(), valueStart: re.lastIndex });
	}
	for (let i = 0; i < keys.length; i++) {
		const valueEnd = i + 1 < keys.length ? cleaned.lastIndexOf(",", keys[i + 1].valueStart - keys[i + 1].key.length - 2) : cleaned.length;
		const value = cleaned.slice(keys[i].valueStart, valueEnd).trim().replace(/,+$/, "");
		if (keys[i].key.length > 0) {
			result.push([keys[i].key, value]);
		}
	}
	return result;
};

const StatusCell = StatusBadge;

const isRefundLikeType = (type?: string | null) => {
	const normalized = type?.toLowerCase();
	return normalized === "refund" || normalized === "return";
};

const formatDateCell = (value: string | null | undefined) => {
	if (!value) return "";
	return formatDisplayDate(value, { fallback: value });
};

const EmailCell = ({ value, visited, clientIp }: { value: string; visited?: string; clientIp?: string }) => { useLocale(); return ((
	<VBox>
		<HBox gap="4px" sx={{ alignItems: "start", overflow: "hidden" }}>
			{visited && (
				<Box sx={{ paddingTop: "0px" }}>
					<svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
						<path d="M2 6.5L4.5 9L10 3" stroke="#16A34A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</Box>
			)}
			<Typo variant="medium">{visited ? msg("visitedLower") : msg("notVisited")}</Typo>
		</HBox>
		<HoverTooltip
			style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
			shouldShow={el => el.scrollWidth > el.clientWidth}
			tooltipStyle={{ padding: "6px 12px", maxWidth: "400px" }}
			content={<span style={{ color: "#FFFFFF", fontSize: "12px", fontWeight: 500, wordBreak: "break-all" }}>{value}</span>}>
			<Typo variant="medium" color="textSecondary">
				{value}
			</Typo>
		</HoverTooltip>
		{clientIp && (
			<Typo variant="caption" sx={{ color: "#667085", fontSize: "11px", overflowWrap: "anywhere" }}>
				{clientIp}
			</Typo>
		)}
	</VBox>
)); };

export interface IDectaSessionsTableProps {
	items: IDectaSession[];
	busy: boolean;
	error?: any;
	headerHeight?: number;
}

export const DectaSessionsTable = ({ items, headerHeight }: IDectaSessionsTableProps) => {
	useLocale();
	const scrollRef = useRef<HTMLDivElement>(null);
	const [showFailReason, setShowFailReason] = useState(true);

	useLayoutEffect(() => {
		const wrapper = scrollRef.current;
		const grid = wrapper?.firstElementChild as HTMLElement | undefined;
		if (!wrapper || !grid) return;

		const measure = () => {
			// Measure the content-sized tracks, reserving a readable email width.
			// Excluding the optional track keeps the decision stable after hiding it.
			const tracks = getComputedStyle(grid).gridTemplateColumns.split(" ").map(parseFloat);
			const fixedWidth = tracks.reduce((sum, width, index) =>
				sum + (index === 4 || (showFailReason && index === 9) ? 0 : width), 0);
			setShowFailReason(fixedWidth + 180 + 160 <= wrapper.clientWidth + 1);
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(wrapper);
		observer.observe(grid);
		Array.from(grid.children).slice(0, showFailReason ? 11 : 10).forEach(header => observer.observe(header));
		return () => observer.disconnect();
	}, [items, showFailReason]);

	const stickyHeadSx = {
		position: "relative" as const,
		zIndex: 2,
		backgroundColor: "#d1dbe6",
		borderTop: "20px solid #f8fafc",
	};

	useEffect(() => {
		const wrapper = scrollRef.current;
		if (!wrapper || items.length === 0) return;

		const grid = wrapper.firstElementChild as HTMLElement;
		if (!grid) return;

		const headerCount = showFailReason ? 11 : 10;
		const headerCells = Array.from(grid.children).slice(0, headerCount) as HTMLElement[];
		if (headerCells.length === 0) return;

		const stickyTop = headerHeight ?? 0;

		const handleScroll = () => {
			const wrapperRect = wrapper.getBoundingClientRect();
			const offset = stickyTop - wrapperRect.top;
			const maxOffset = grid.offsetHeight - headerCells[0].offsetHeight;
			const clamped = Math.max(0, Math.min(offset, maxOffset));

			for (const cell of headerCells) {
				cell.style.transform = clamped > 0 ? `translateY(${clamped}px)` : '';
			}
		};

		window.addEventListener('scroll', handleScroll, { passive: true });
		handleScroll();

		return () => window.removeEventListener('scroll', handleScroll);
	}, [items, headerHeight, showFailReason]);

	return (
		<>
			{items.length > 0 && (
				<Box sx={{ flex: '1 1 100%', minWidth: 0, boxSizing: 'border-box' }}>
					<div ref={scrollRef} style={{ overflowX: "auto" }}>
						<Table<IDectaSession>
							headPadding="8px 12px"
							cellPadding="8px 12px"
							sx={{ minWidth: "min-content" }}
							onRowClick={row => {
								S_DECTA_SESSION_POPUP.invoke({ sessionId: row.id });
							}}
							headerFactory={val => {
								if (typeof val === "string") {
									return {
										content: (
											<Typo
												variant="medium"
												sx={{
													fontWeight: 700,
													fontFamily: "monospace",
													fontSize: "11px",
													textTransform: "uppercase",
													letterSpacing: "0.03em",
												}}
											>
												{val === "settlement_status" ? "SETTLEMENT" : val.replace(/_/g, " ").toUpperCase()}
											</Typo>
										)
									};
								}
								return { content: null };
							}}
							headers={[
								{ value: "id", gridColumn: "max-content", sx: stickyHeadSx },
								{ value: "country", gridColumn: "max-content", sx: stickyHeadSx },
								{ value: "card_network", gridColumn: "max-content", sx: stickyHeadSx },
								{ value: "decta_payment_id", gridColumn: "max-content", sx: stickyHeadSx },
								{ value: "email", gridColumn: "minmax(180px, 1fr)", sx: stickyHeadSx },
								{ value: "merchant_id", gridColumn: "max-content", sx: stickyHeadSx },
								{ value: "type", gridColumn: "max-content", sx: stickyHeadSx },
								{ value: "amount", gridColumn: "max-content", sx: stickyHeadSx },
								{ value: "status", gridColumn: "max-content", sx: stickyHeadSx },
								...(showFailReason ? [{ value: "fail_reason", gridColumn: "160px", sx: stickyHeadSx }] : []),
								{ value: "settlement_status", gridColumn: "max-content", sx: stickyHeadSx },
							]}
							order={
								[
									"id",
									"country",
									"card_network",
									"decta_payment_id",
									"email",
									"merchant_id",
									"type",
									"amount",
									"status",
									...(showFailReason ? ["fail_reason"] : []),
									"settlement_status",
								] as any
							}
							cellFactory={(row, cell, _index, _key, name) => {
							if (name === "id") {
								return {
									content: (
										<VBox>
											<Typo variant="medium"><DateTimeTooltip value={row.created_at}>{formatDisplayDateTime(row.created_at, { separator: " - " })}</DateTimeTooltip></Typo>
											<Typo variant="caption" color="textSecondary">
												{row.id}
											</Typo>
										</VBox>
									),
								};
							}
							if (name === "card_network") {
								let icon = null;
								if (row.card_network.toLowerCase() === "mastercard") {
									icon = <IconMastercard />;
								} else if (row.card_network.toLowerCase() === "visa") {
									icon = <IconVisa />;
								}
								return {
									content: (
										<HBox gap="8px" sx={{ alignItems: "center" }}>
											<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "32px", flex: "0 0 32px" }}>{icon}</Box>
											<VBox gap="2px">
												<Typo variant="medium">{row.card_mask}</Typo>
												{row.card_token && (
													<span
														title={row.card_token}
														style={{
															fontSize: "11px",
															lineHeight: "15px",
															color: row.tokenized === false || row.tokenized === 0 ? "#b91c1c" : "#667085",
															maxWidth: "180px",
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}>
														{row.card_token}
													</span>
												)}
											</VBox>
										</HBox>
									),
								};
							}
							if (name === "type") {
								const isRefundLike = isRefundLikeType(row.type);
								const isDms = row.type?.toLowerCase() === "dms";
								const isSms = row.type?.toLowerCase() === "sms";
								const isTransfer = row.type?.toLowerCase() === "transfer";
								const bgColor = isRefundLike
									? "rgba(38, 102, 220, 0.12)"
									: isSms
										? "rgba(124, 58, 237, 0.12)"
									: isDms || isTransfer
										? "rgba(22,163,74,0.12)"
										: "rgba(100,116,139,0.12)";
								const textColor = isRefundLike ? "#154fbb" : isSms ? "#7C3AED" : isDms || isTransfer ? "#16A34A" : "#64748B";
								return {
									content: (
										<VBox gap="3px" sx={{ alignItems: "flex-start" }}>
										<Box
											sx={{
												display: "inline-flex",
												alignItems: "center",
												padding: "2px 10px",
												borderRadius: "12px",
												backgroundColor: bgColor,
											}}>
											<Typo variant="caption" sx={{ color: textColor, fontWeight: 600 }}>
												{row.type?.toUpperCase()}
											</Typo>
										</Box>
										{row.substatus && (
											<Typo variant="caption" sx={{ fontSize: "10px", lineHeight: "13px", color: "#667085" }}>
												{row.substatus}
											</Typo>
										)}
										</VBox>
									),
								};
							}
							if (name === "amount") {
								const isRefundLike = isRefundLikeType(row.type);
								const isDms = row.type?.toLowerCase() === "dms";
								const isTransfer = row.type?.toLowerCase() === "transfer";
								const amountColor = isRefundLike ? "#DC2626" : isDms || isTransfer ? "#16A34A" : undefined;
								const prefix = isRefundLike ? "-" : "";
								return {
									content: (
										<Typo variant="medium" sx={amountColor ? { color: amountColor, fontWeight: 600 } : {}}>
											{prefix + (row.amount / 100).toFixed(2) + " " + row.currency}
										</Typo>
									),
								};
							}
							if (name === "email_id" || name === "email") {
								return {
									sx: { minWidth: 0 },
									content: <EmailCell value={cell == null ? "" : String(cell)} visited={row.payer_token} clientIp={row.client_ip} />,
								};
							}
							if (name === "country") {
								return {
									content: <CountryClip code={row.country} />,
									sx: { padding: "10px" },
								};
							}
							if (name === "merchant_id") {
								return {
									content: <Typo variant="medium">{cell}</Typo>,
								};
							}
							if (name === "decta_payment_id") {
								return {
									content: <Typo variant="medium">{cell}</Typo>,
								};
							}

							if (name === "fail_reason") {
								return {
									sx: { minWidth: 0, overflow: "hidden" },
									content: row.fail_reason ? (
										<HoverTooltip
											tooltipStyle={{ maxWidth: "500px", width: "max-content" }}
											content={<span style={{ color: "#fff", fontSize: "12px", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{row.fail_reason}</span>}>
											<Typo variant="caption" sx={{ display: "block", color: "#b91c1c", opacity: 1, fontSize: "12px", lineHeight: "16px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "help" }}>
												{cleanRaw(row.fail_reason)}
											</Typo>
										</HoverTooltip>
									) : <Typo variant="caption" sx={{ color: "#98a2b3" }}>—</Typo>,
								};
							}
							if (name === "status") {
								const isFailed = row.status?.toLowerCase() === "failed";
								const isAfError = row.antifraud_status === "error";
								const statusContent = (
									<HBox gap="6px" sx={{ alignItems: "center" }}>
										{isFailed && isAfError && (
											<Box
												sx={{
													backgroundColor: "rgba(220,38,38,0.12)",
													color: "#DC2626",
													fontWeight: 700,
													fontSize: "10px",
													borderRadius: "50%",
													width: "22px",
													height: "22px",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													flexShrink: 0,
												}}>
												AF
											</Box>
										)}
										<StatusCell value={row.status} />
										{row.chargeback_status && row.chargeback_status.toLowerCase() !== "unknown" && (
											<span
												title={msg("templateChargebackDate_3c1b72d7", { v0: row.chargeback_status, v1: row.chargeback_date || "—" })}
												aria-label={msg("templateChargebackDate_3f58f2d7", { v0: row.chargeback_status, v1: row.chargeback_date || "—" })}
												tabIndex={0}
												style={{ display: "inline-flex", color: "#b54708", cursor: "help" }}>
												<CircleAlert size={14} aria-hidden="true" />
											</span>
										)}
										{(row.fail_reason || (isFailed && isAfError)) && <FailReasonHint />}
									</HBox>
								);
								if (row.fail_reason || (isFailed && isAfError)) {
									const entries = row.fail_reason ? parseFailReason(row.fail_reason) : [];
									return {
										content: (
											<HoverTooltip
												align="end"
												style={{ cursor: "pointer" }}
												tooltipStyle={{ minWidth: "240px", maxWidth: "380px" }}
												content={
													<VBox gap="6px">
														{isAfError && (
															<Typo variant="medium" sx={{ color: "#F87171", fontWeight: 600 }}>{msg("antifraudError")}</Typo>
														)}
														{entries.length > 0 ? (
															<table style={{ borderCollapse: "collapse", width: "100%" }}>
																<tbody>
																	{entries.map(([key, value], i) => (
																		<tr key={i}>
																			<td
																				style={{
																					color: "#9CA3AF",
																					fontSize: "12px",
																					fontWeight: 500,
																					whiteSpace: "nowrap",
																					paddingRight: "12px",
																					paddingTop: "3px",
																					paddingBottom: "3px",
																					verticalAlign: "top",
																				}}>
																				{key}
																			</td>
																			<td
																				style={{
																					color: "#FFFFFF",
																					fontSize: "12px",
																					fontWeight: 500,
																					wordBreak: "break-word",
																					paddingTop: "3px",
																					paddingBottom: "3px",
																					verticalAlign: "top",
																				}}>
																				{value}
																			</td>
																		</tr>
																	))}
																</tbody>
															</table>
														) : row.fail_reason ? (
															<Typo variant="medium" sx={{ color: "#FFFFFF" }}>
																{cleanRaw(row.fail_reason)}
															</Typo>
														) : null}
													</VBox>
												}>
												{statusContent}
											</HoverTooltip>
										),
									};
								}
								return { content: statusContent };
							}
							if (name === "settlement_status") {
								const statusText = row.settlement_status ? row.settlement_status : row.reco_id > 0 ? "submitted" : "";
								return {
									content: (
										<VBox gap="3px" sx={{ alignItems: "flex-start" }}>
											<StatusCell value={statusText} />
											{row.settlement_date && (
												<Typo variant="caption" sx={{ fontSize: "12px", fontWeight: 500, color: "#475467", opacity: 1, whiteSpace: "nowrap" }}>
													<DateTimeTooltip value={row.settlement_date}>{formatDateCell(row.settlement_date)}</DateTimeTooltip>
												</Typo>
											)}
										</VBox>
									),
								};
							}

							return {
								content: cell,
							};
						}}
							data={items}
						/>
					</div>
				</Box>
			)}
		</>
	);
};
